import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AIPanel from "../../../components/AIChat/AIPanel";
import { DialogControl, IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useGenericManager, useOperationDialog } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { sortOperations } from "../../../utilities/operationSort";
import ColumnSetEditor from "../../../components/ColumnSetEditor";
import { hardRedirectTo } from "./screenNavigation";
import "./sourceColumnSetManage.scss";

const firstItem = (value) => {
  if (!value) return null;
  if (typeof value?.first === "function") return value.first();
  return Array.isArray(value) ? value[0] : null;
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const firstRedirectItem = (value) => {
  const parsed = parseMaybeJson(value);
  return firstItem(parsed) || parsed || null;
};

const getEntityObjectId = (value) =>
  value?.EntityObjectId || value?._id || value?.UniqueId || value?.Id || value?.id || null;

const getCurrentRelativeUrl = () => {
  if (typeof window === "undefined" || !window.location) return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}` || "/";
};

const getRawBackUrl = (searchParams) =>
  searchParams.get("backUrl") ||
  searchParams.get("backurl") ||
  searchParams.get("returnUrl") ||
  searchParams.get("returnurl") ||
  "";

const resolveBackUrl = (searchParams) => {
  const rawBackUrl = getRawBackUrl(searchParams);

  if (!rawBackUrl) return getCurrentRelativeUrl();

  let decodedBackUrl = rawBackUrl;
  try {
    decodedBackUrl = decodeURIComponent(rawBackUrl);
  } catch {
    decodedBackUrl = rawBackUrl;
  }

  if (decodedBackUrl.startsWith("/") && !decodedBackUrl.startsWith("//")) {
    return decodedBackUrl;
  }

  try {
    const url = new URL(decodedBackUrl, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Fall through to current page fallback.
  }

  return getCurrentRelativeUrl();
};

const hasExplicitBackUrl = (searchParams) => Boolean(getRawBackUrl(searchParams));

const normalizeParamsObject = (source) => {
  const parsed = parseMaybeJson(source);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  return parsed;
};

const getPanelParam = (params, ...keys) => {
  if (!params || typeof params !== "object") return "";
  for (const key of keys) {
    const value = params[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

const getCustomField = (item, key) => {
  const customFields = item?.CustomFields || item?.customFields || {};
  return customFields?.[key] ?? customFields?.[key?.toLowerCase?.()] ?? null;
};

const isEdiEntity = (item) =>
  String(getCustomField(item, "EdiDomain") || getCustomField(item, "DestinationApiType") || "").toLowerCase() === "edi" ||
  Boolean(getCustomField(item, "EDIWriterSpecId"));

const markEdiAlpha = (item) => {
  if (!isEdiEntity(item)) return item;
  return {
    ...item,
    CustomFields: {
      ...(item?.CustomFields || {}),
      EDIReadinessStatus: "Alpha"
    }
  };
};

const ENTITY_OBJECT_ITEM_NAME = "EntityObjectItem";
const PANEL_TRANSITION_MS = 260;
const ENTITY_OBJECT_MODIFY_PANEL_WIDTH = 1225;
const EXCLUDED_ENTITY_OPERATION_IDS = new Set([
  "ModifyEntity",
  "AIQuestionEntityObjectModify",
  "AIQuestionEntityObject",
]);
const EXCLUDED_ENTITY_OPERATION_LABELS = new Set([
  "Modify Entity",
  "Modify Entity Configuration",
  "Ask Your Warehouse Data",
]);

const getOperationName = (operation) => operation?.Name || operation?.OperationTypeId || operation?.Value || operation?._id || operation?.Id || "";

const getOperationDisplayName = (operation) => operation?.DisplayName || operation?.Text || getOperationName(operation);

const isEntityObjectOperation = (operation) =>
  operation?.SupportsOperationItem === true &&
  (operation?.ItemName === ENTITY_OBJECT_ITEM_NAME || operation?.MetadataRefItemName === ENTITY_OBJECT_ITEM_NAME);

const isExcludedEntityOperation = (operation) =>
  EXCLUDED_ENTITY_OPERATION_IDS.has(getOperationName(operation)) ||
  EXCLUDED_ENTITY_OPERATION_LABELS.has(getOperationDisplayName(operation));

const normalizeOperationForDialog = (operation) => {
  const name = getOperationName(operation);
  return {
    ...operation,
    Name: name,
    OperationTypeId: operation?.OperationTypeId || name,
    DisplayName: getOperationDisplayName(operation),
  };
};

const EntityObjectModify = ({
  panelMode = false,
  panelOpen = true,
  panelId = "",
  panelSessionId = "",
  panelParams = null,
  onPanelClose = null
}) => {
  const itemService = useMemo(() => new ItemServiceFactory(), []);
  const itemServiceEnum = itemService?.ItemServiceEnum ?? {
    EntityObject: "EntityObject",
    AIChatSession: "AIChatSession",
  };
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const navigate = useNavigate();
  const coreState = useCoreStateContext();
  const coreContext = useCoreContext();
  const pageRef = useRef(null);

  const entityObjectMgr = useGenericManager(itemServiceEnum.EntityObject);
  const chatSessionMgr = useGenericManager(itemServiceEnum.AIChatSession);
  const {
    handleOperationDialog,
    activeOperation,
    operationSuccessMessage,
    operationErrors,
    cancelOperation,
    renderOperationContent,
    executeOperation,
    openDialog,
  } = useOperationDialog(itemServiceEnum.EntityObject);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const chatSessionMgrRef = useRef(chatSessionMgr);
  const coreStateRef = useRef(coreState);
  entityObjectMgrRef.current = entityObjectMgr;
  chatSessionMgrRef.current = chatSessionMgr;
  coreStateRef.current = coreState;

  const [entityObject, setEntityObject] = useState(null);
  const [currentEntityObject, setCurrentEntityObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState("0");
  const [session, setSession] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [panelContentReady, setPanelContentReady] = useState(!panelMode);

  const { id } = useParams();
  const backUrl = useMemo(() => resolveBackUrl(searchParams), [searchParamsKey, searchParams]);
  const useHardBackRedirect = useMemo(() => hasExplicitBackUrl(searchParams), [searchParamsKey, searchParams]);
  const resolvedPanelParams = useMemo(() => normalizeParamsObject(panelParams), [panelParams]);
  const effectiveId = useMemo(() => (
    String(panelId ?? "").trim() ||
    getPanelParam(resolvedPanelParams, "entityObjectId", "EntityObjectId", "id", "_id") ||
    String(searchParams.get("entityObjectId") ?? searchParams.get("id") ?? "").trim() ||
    String(id ?? "").trim()
  ), [id, panelId, resolvedPanelParams, searchParams]);
  const lastPayloadStrRef = useRef("");
  const loadKeyRef = useRef(null);
  const canLoadData = !panelMode || panelContentReady;

  const entityObjectOperations = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    return sortOperations(operations
      .filter(isEntityObjectOperation)
      .filter((operation) => !isExcludedEntityOperation(operation))
      .map(normalizeOperationForDialog));
  }, [coreContext?.Operations]);

  useEffect(() => {
    if (!panelMode) {
      setPanelContentReady(true);
      return undefined;
    }

    if (!panelOpen) {
      setPanelContentReady(false);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setPanelContentReady(true);
    }, PANEL_TRANSITION_MS);

    return () => window.clearTimeout(timerId);
  }, [panelMode, panelOpen]);

  const setCurrentEntityObjectIfChanged = useCallback((next) => {
    let nextStr = "";
    try {
      nextStr = JSON.stringify(next);
    } catch {
      /* ignore */
    }

    if (nextStr && nextStr === lastPayloadStrRef.current) return;

    setCurrentEntityObject((prev) => {
      try {
        const prevStr = JSON.stringify(prev);
        if (prevStr === nextStr) return prev;
      } catch {
        if (prev === next) return prev;
      }
      lastPayloadStrRef.current = nextStr || "";
      return next;
    });
  }, []);

  useEffect(() => {
    if (!canLoadData) {
      return undefined;
    }

    if (!effectiveId) {
      setIsLoading(false);
      setEntityObject(null);
      setCurrentEntityObjectIfChanged(null);
      return undefined;
    }

    const loadKey = `${panelMode ? "panel" : "route"}|${effectiveId}|${panelSessionId || ""}`;
    if (loadKeyRef.current === loadKey) {
      return undefined;
    }

    loadKeyRef.current = loadKey;
    let cancelled = false;

    const getChatSession = async (referenceId) => {
      if (!referenceId) return null;
      if (panelSessionId) {
        return {
          _id: panelSessionId,
          ReferenceId: referenceId,
          ReferenceItemName: "EntityObjectModify"
        };
      }

      const chatSessionResult = await chatSessionMgrRef.current.Query({
        model:
          "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
        params: { referenceId, referenceItemName: "EntityObjectModify" },
      });

      if (chatSessionResult?.Success && chatSessionResult?.Items?.length > 0) {
        return firstItem(chatSessionResult.Items);
      }
      return null;
    };

    const load = async () => {
      setIsLoading(true);

      try {
        let nextEntityObject = null;
        const redirectData =
          firstRedirectItem(coreStateRef.current?.get?.("InternalRedirectData")) ||
          firstRedirectItem(sessionStorage.getItem("InternalRedirectData"));
        if (!panelMode && redirectData?.EntityObjectId && (effectiveId === redirectData._id || effectiveId === redirectData.EntityObjectId)) {
          nextEntityObject = redirectData;
          coreStateRef.current?.remove?.("InternalRedirectData");
          sessionStorage.removeItem("InternalRedirectData");
        } else {
          const result = await entityObjectMgrRef.current.getItem(effectiveId);
          if (result?.Success && result.Items?.length > 0) {
            nextEntityObject = firstItem(result.Items);
          }
        }

        if (!cancelled && nextEntityObject) {
          setEntityObject(nextEntityObject);
          setCurrentEntityObjectIfChanged(nextEntityObject);
          const nextSession = await getChatSession(getEntityObjectId(nextEntityObject));
          if (!cancelled) setSession(nextSession);
        }

        const metadataResp = await entityObjectMgrRef.current.GetMetadata({});
        if (!cancelled) setMetadata(metadataResp?.Items ?? []);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [canLoadData, effectiveId, panelMode, panelSessionId, setCurrentEntityObjectIfChanged]);

  const onUpdatePanel = async (UICommand, item, value) => {
    if (UICommand !== "update") return;

    const normalizeDraftPayload = (candidate) => {
      if (!candidate || typeof candidate !== "object") return null;
      if (Array.isArray(candidate.Fields)) return candidate;
      if (Array.isArray(candidate.fields)) return { ...candidate, Fields: candidate.fields };
      return null;
    };

    const updatedEntityObject =
      normalizeDraftPayload(item) ??
      normalizeDraftPayload(value) ??
      normalizeDraftPayload(value?.entityObject) ??
      normalizeDraftPayload(value?.output) ??
      normalizeDraftPayload(value?.output?.entityObject) ??
      null;

    if (updatedEntityObject?.Fields) {
      setCurrentEntityObjectIfChanged(updatedEntityObject);
      setRefresh({ ...updatedEntityObject, acceptedAt: Date.now() });
    }
  };

  const handleSaveEntityObject = async (updatedEntityObject) => {
    const itemToSave = markEdiAlpha(updatedEntityObject);
    const result = await entityObjectMgr.saveItem(itemToSave);
    if (result?.Success) {
      const saved = firstItem(result.Items);
      setRefresh(String(Date.now()));
      setEntityObject(saved);
      setCurrentEntityObjectIfChanged(saved);
    }
    return result;
  };

  const handleCurrentEntityObject = (value) => {
    setCurrentEntityObjectIfChanged(value);
  };

  const handleEntityOperationClick = async (operation) => {
    if (!operation || !entityObject) return;

    await handleOperationDialog(
      {
        ...operation,
        itemData: currentEntityObject || entityObject,
      },
      { closeOnComplete: false }
    );
  };

  const entityObjectId = getEntityObjectId(entityObject) || id;
  const handleBack = useCallback(() => {
    if (useHardBackRedirect && hardRedirectTo(backUrl)) return;
    navigate(backUrl);
  }, [backUrl, navigate, useHardBackRedirect]);

  const handleCancel = useCallback(() => {
    if (panelMode) {
      if (typeof onPanelClose === "function") onPanelClose();
      return;
    }

    handleBack();
  }, [handleBack, onPanelClose, panelMode]);

  const renderPanelContent = () => {
    if (isLoading) {
      return (
        <div className="centered-loading" ref={pageRef}>
          <Loading />
        </div>
      );
    }

    return (
      <div className="column-set-editor" ref={pageRef}>
        <ColumnSetEditor
          sourceObject={entityObject}
          fields={entityObject?.Fields ?? []}
          fieldItemMetadata={metadata?.find((x) => x.Name === "EntityObjectField")}
          sourceObjectItemMetadata={metadata?.find((x) => x.Name === "EntityObject")}
          onSave={handleSaveEntityObject}
          onChange={handleCurrentEntityObject}
          refresh={refresh}
          sourceObjectOperations={entityObjectOperations}
          onSourceObjectOperationClick={handleEntityOperationClick}
          objectLabel="Entity Object"
          objectDesignerName="Entity Object Editor"
          activeTitle="Active Fields"
          activeSubtitle="Fields currently shown on the entity grid."
          availableTitle="Available Fields"
          availableSubtitle="Fields not currently shown on the entity grid."
          objectActionsLabel="Entity Object Actions"
          noObjectActionsLabel="No entity actions available"
          editObjectTitle="Edit Entity Object"
          saveImpactMessage="Saving this entity configuration may affect mappings, validation, and generated EDI behavior."
          storageKeyPrefix="EntityObjectModify:"
          titleVersion={entityObject?.Version ?? entityObject?.version ?? ""}
          titleVersionPrefix=""
          responsiveAvailablePickerEnabled={true}
          sidePanelEditingEnabled={!panelMode}
          fieldEditingPresentation={panelMode ? "inline" : "sidePanel"}
        />
      </div>
    );
  };

  if (!panelMode && isLoading) {
    return (
      <div className="centered-loading">
        <Loading />
      </div>
    );
  }

  return (
    <div className="source-column-set-screen">
      {!panelMode ? (
        <div className="source-column-set-screen__header">
        <h2 className="source-column-set-screen__title">
          <IconControl iconKey={"schema"} /> Modify Entity
        </h2>
        <div className="source-column-set-screen__header-actions">
          <button type="button" className="source-column-set-screen__back" onClick={handleBack}>
            {"<<"} Back
          </button>
        </div>
      </div>
      ) : null}
      <DialogControl
        controlOrigin="entity-object-execute-operation"
        openDialog={openDialog}
        title={activeOperation?.DisplayName ?? activeOperation?.Name ?? "Operation Completed"}
        subTitle={activeOperation?.Details}
        onCancel={cancelOperation}
        onOk={async () => executeOperation()}
        excludeCancel={!activeOperation}
        okText={activeOperation && operationErrors ? "Retry" : "OK"}
        disableCancel={operationSuccessMessage}
      >
        {renderOperationContent()}
      </DialogControl>
      <AIPanel
        childrenRef={pageRef}
        open={panelMode ? panelOpen : true}
        presentation={panelMode ? "slideout" : "inline"}
        title="Modify Entity"
        className={panelMode ? "ai-panel-floating-slideout source-column-set-panel" : ""}
        onUpdatePanel={onUpdatePanel}
        onSessionChange={(sessionId) => {
          if (sessionId) {
            setSession({
              _id: sessionId,
              ReferenceId: entityObjectId,
              ReferenceItemName: "EntityObjectModify"
            });
          }
        }}
        mapToClass="core-layout"
        width={panelMode ? ENTITY_OBJECT_MODIFY_PANEL_WIDTH : undefined}
        minPanelWidth={panelMode ? ENTITY_OBJECT_MODIFY_PANEL_WIDTH : undefined}
        panelStorageKey={panelMode ? null : undefined}
        contentReady={panelMode ? panelContentReady : true}
        params={{ id: entityObjectId, entityObjectId }}
        session={session}
        payload={currentEntityObject}
        referenceId={entityObjectId}
        referenceName="EntityObjectModify"
        operationName="AIQuestionEntityObjectModify"
        defaultOrchestrationMode="AgentAssist"
        singleSessionMode={true}
        planningPreviewEnabled={false}
        enableContentDragHiding={false}
        minLeft={825}
        minRight={400}
        startChat={null}
        splashMessage="Paste a client spec in chat or describe the entity changes to draft."
        introMessage="Modify Entity..."
        onCancel={handleCancel}
      >
        {renderPanelContent()}
      </AIPanel>
    </div>
  );
};

export default EntityObjectModify;
